import { onMounted, onUnmounted } from 'vue';
import type { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import { BarcodeScanner } from '@/native/plugins/BarcodeScanner';
import type { ScanResult } from '@/native/plugins/BarcodeScanner/definitions';
import { AccessoryType, DeviceTypeEnum } from '@/enums/device';
import { parseCode } from '@/utils/scanner/codeParser';
import { isHardwareScanPaused } from './hardwareScanCoordinator';

type DeviceTypeArg =
  | AccessoryType
  | DeviceTypeEnum
  | undefined
  | (() => AccessoryType | DeviceTypeEnum | undefined);

export default function useHardwareScanListener(
  onScan: (parsed: Record<string, unknown>) => void,
  deviceType?: DeviceTypeArg
) {
  let handle: PluginListenerHandle | undefined;
  let started = false;

  const resolveDeviceType = () =>
    typeof deviceType === 'function' ? deviceType() : deviceType;

  onMounted(async () => {
    if (!Capacitor.isNativePlatform()) return;

    try {
      const { available } = await BarcodeScanner.isHardwareScannerAvailable();
      if (!available) return;

      handle = await BarcodeScanner.addListener(
        'hardwareScan',
        (event: ScanResult) => {
          if (isHardwareScanPaused()) return;
          if (!event?.hasContent) return;
          onScan(parseCode(event, resolveDeviceType()));
        }
      );
      await BarcodeScanner.startHardwareListener();
      started = true;
    } catch (error) {
      console.warn('Hardware scan listener setup failed', error);
    }
  });

  onUnmounted(async () => {
    try {
      await handle?.remove();
    } catch (error) {
      console.warn('Hardware scan listener removal failed', error);
    }

    if (started) {
      try {
        await BarcodeScanner.stopHardwareListener();
      } catch (error) {
        console.warn('Stopping hardware scan listener failed', error);
      }
    }
  });
}
